import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Chip,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Collapse,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link } from 'react-router-dom';
import { getSupplierFinance, markSupplierPOsPaid } from '../../collections/suppliers';
import { updatePurchaseOrderItemCosts } from '../../collections/purchaseOrders';

const SupplierFinanceTab = ({ supplierId, onPaymentComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [expandedPOs, setExpandedPOs] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Payment reference code state
  const [paymentReferenceCode, setPaymentReferenceCode] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  // Edit mode state
  const [editingPO, setEditingPO] = useState(null); // PO ID being edited
  const [editedCosts, setEditedCosts] = useState({}); // { itemId: newCost }
  const [savingCosts, setSavingCosts] = useState(false);

  useEffect(() => {
    fetchFinanceData();
  }, [supplierId]);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSupplierFinance(supplierId);
      setFinanceData(response.data);
      setSelectedPOs([]);
      setExpandedPOs([]);
    } catch (err) {
      setError(err.message || 'Lỗi khi tải dữ liệu tài chính');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (poId) => {
    setExpandedPOs(prev => {
      if (prev.includes(poId)) {
        return prev.filter(id => id !== poId);
      } else {
        return [...prev, poId];
      }
    });
  };

  const handleExpandAll = () => {
    if (expandedPOs.length === financeData?.unpaid_purchase_orders?.length) {
      setExpandedPOs([]);
    } else {
      setExpandedPOs(financeData?.unpaid_purchase_orders?.map(po => po.id) || []);
    }
  };

  // Edit mode handlers
  const handleStartEdit = (po) => {
    // Initialize edited costs with current values
    const initialCosts = {};
    po.items?.forEach(item => {
      initialCosts[item.id] = item.unit_cost;
    });
    setEditedCosts(initialCosts);
    setEditingPO(po.id);
    // Auto-expand the PO being edited
    if (!expandedPOs.includes(po.id)) {
      setExpandedPOs(prev => [...prev, po.id]);
    }
  };

  const handleCancelEdit = () => {
    setEditingPO(null);
    setEditedCosts({});
  };

  const handleCostChange = (itemId, value) => {
    setEditedCosts(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleSaveCosts = async (poId) => {
    try {
      setSavingCosts(true);
      setError(null);

      // Build items array with updated costs
      const items = Object.entries(editedCosts).map(([itemId, unitCost]) => ({
        id: parseInt(itemId),
        unit_cost: parseFloat(unitCost) || 0
      }));

      await updatePurchaseOrderItemCosts(poId, items);

      // Refresh data
      await fetchFinanceData();

      // Exit edit mode
      setEditingPO(null);
      setEditedCosts({});
    } catch (err) {
      setError(err.message || 'Lỗi khi cập nhật đơn giá');
    } finally {
      setSavingCosts(false);
    }
  };

  const getEditedLineTotal = (item) => {
    const cost = editedCosts[item.id] !== undefined ? parseFloat(editedCosts[item.id]) || 0 : item.unit_cost;
    return cost * item.quantity_ordered;
  };

  const getEditedPOTotal = (po) => {
    return po.items?.reduce((sum, item) => sum + getEditedLineTotal(item), 0) || 0;
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPOs(financeData.unpaid_purchase_orders.map(po => po.id));
    } else {
      setSelectedPOs([]);
    }
  };

  const handleSelectPO = (poId) => {
    setSelectedPOs(prev => {
      if (prev.includes(poId)) {
        return prev.filter(id => id !== poId);
      } else {
        return [...prev, poId];
      }
    });
  };

  const getSelectedTotal = () => {
    if (!financeData) return 0;
    return financeData.unpaid_purchase_orders
      .filter(po => selectedPOs.includes(po.id))
      .reduce((sum, po) => sum + (po.total_amount || 0), 0);
  };

  // Generate payment reference code (format: NCCYYYYXXXX)
  const generatePaymentReferenceCode = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000); // 4 digit random
    return `NCC${year}${random}`;
  };

  const handleOpenPaymentDialog = () => {
    // Generate reference code when opening dialog
    setPaymentReferenceCode(generatePaymentReferenceCode());
    setCopiedCode(false);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentNotes('');
    setPaymentReferenceCode('');
    setCopiedCode(false);
  };

  const handleConfirmPayment = async () => {
    try {
      setSubmitting(true);
      await markSupplierPOsPaid(supplierId, {
        purchase_order_ids: selectedPOs,
        notes: paymentNotes || null,
        payment_reference_code: paymentReferenceCode
      });
      handleClosePaymentDialog();
      await fetchFinanceData();
      if (onPaymentComplete) {
        onPaymentComplete();
      }
    } catch (err) {
      setError(err.message || 'Lỗi khi xử lý thanh toán');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(paymentReferenceCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'DRAFT': 'Nháp',
      'SENT': 'Đã gửi',
      'PARTIALLY_RECEIVED': 'Nhận một phần',
      'FULLY_RECEIVED': 'Đã nhận đủ',
      'CLOSED': 'Đã đóng',
      'CANCELLED': 'Đã hủy'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'DRAFT': '#6b8e4e',
      'SENT': '#0e2a04',
      'PARTIALLY_RECEIVED': '#d4a574',
      'FULLY_RECEIVED': '#1a4f0a',
      'CLOSED': '#8b7355',
      'CANCELLED': '#d32f2f'
    };
    return colorMap[status] || '#666666';
  };

  const StatCard = ({ title, value, subValue, icon, color = '#0e2a04' }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              color="#666666"
              variant="body2"
              sx={{
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.75rem',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{
                mt: 1,
                fontWeight: 700,
                color: color,
                fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              {value}
            </Typography>
            {subValue && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subValue}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
              width: 56,
              height: 56,
              boxShadow: `0 2px 4px ${color}33`
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: '12px' }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const selectedPOsData = financeData?.unpaid_purchase_orders?.filter(po => selectedPOs.includes(po.id)) || [];

  return (
    <Box sx={{ p: 0 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Tổng nợ chưa thanh toán"
            value={formatCurrency(financeData?.total_unpaid_amount || 0)}
            subValue={`${financeData?.unpaid_count || 0} đơn hàng`}
            icon={<AccountBalanceIcon />}
            color="#d32f2f"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Số PO chưa thanh toán"
            value={financeData?.unpaid_count || 0}
            icon={<ReceiptIcon />}
            color="#d4a574"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Đã thanh toán tháng này"
            value={formatCurrency(financeData?.paid_this_month_amount || 0)}
            subValue={`${financeData?.paid_this_month_count || 0} đơn hàng`}
            icon={<CheckCircleIcon />}
            color="#1a4f0a"
          />
        </Grid>
      </Grid>

      {/* Unpaid POs Table */}
      <Card
        sx={{
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              background: '#f4f1e8',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1E293B',
                fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Đơn hàng chưa thanh toán ({financeData?.unpaid_count || 0})
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {financeData?.unpaid_purchase_orders?.length > 0 && (
                <Button
                  size="small"
                  onClick={handleExpandAll}
                  startIcon={expandedPOs.length === financeData?.unpaid_purchase_orders?.length ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', color: '#666' }}
                >
                  {expandedPOs.length === financeData?.unpaid_purchase_orders?.length ? 'Thu gọn tất cả' : 'Mở rộng tất cả'}
                </Button>
              )}
              {selectedPOs.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Đã chọn: {selectedPOs.length} PO | Tổng: {formatCurrency(getSelectedTotal())}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handleOpenPaymentDialog}
                    sx={{
                      background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a4f0a 0%, #0e2a04 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(14, 42, 4, 0.3)'
                      }
                    }}
                  >
                    Đánh dấu đã thanh toán
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {financeData?.unpaid_purchase_orders?.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: '#fafafa',
                      '& .MuiTableCell-head': {
                        fontWeight: 600,
                        color: '#1a1a1a',
                        borderBottom: '2px solid rgba(14, 42, 4, 0.1)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
                      }
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ width: 50 }} />
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedPOs.length > 0 && selectedPOs.length < financeData.unpaid_purchase_orders.length}
                        checked={financeData.unpaid_purchase_orders.length > 0 && selectedPOs.length === financeData.unpaid_purchase_orders.length}
                        onChange={handleSelectAll}
                        sx={{
                          color: '#0e2a04',
                          '&.Mui-checked': { color: '#0e2a04' },
                          '&.MuiCheckbox-indeterminate': { color: '#0e2a04' }
                        }}
                      />
                    </TableCell>
                    <TableCell>Mã PO</TableCell>
                    <TableCell>Ngày đặt</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell>Sản phẩm</TableCell>
                    <TableCell align="right">Số tiền</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financeData.unpaid_purchase_orders.map((po) => (
                    <React.Fragment key={po.id}>
                      <TableRow
                        hover
                        selected={selectedPOs.includes(po.id)}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(14, 42, 4, 0.08)',
                            '&:hover': {
                              backgroundColor: 'rgba(14, 42, 4, 0.12)'
                            }
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpand(po.id)}
                            sx={{ color: '#666' }}
                          >
                            {expandedPOs.includes(po.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedPOs.includes(po.id)}
                            onChange={() => handleSelectPO(po.id)}
                            sx={{
                              color: '#0e2a04',
                              '&.Mui-checked': { color: '#0e2a04' }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/purchase-orders/${po.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#0e2a04',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  color: '#1a4f0a'
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {po.po_number}
                              </Typography>
                              <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                            </Box>
                          </Link>
                        </TableCell>
                        <TableCell>{formatDate(po.order_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(po.status)}
                            size="small"
                            sx={{
                              backgroundColor: `${getStatusColor(po.status)}20`,
                              color: getStatusColor(po.status),
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={`${po.fully_received_count || 0}/${po.items_count || 0} sản phẩm đã nhận đủ`}>
                              <Chip
                                icon={<InventoryIcon sx={{ fontSize: 16 }} />}
                                label={`${po.items_count || 0} SP`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: po.fully_received_count === po.items_count ? '#1a4f0a' : '#d4a574',
                                  color: po.fully_received_count === po.items_count ? '#1a4f0a' : '#d4a574',
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Tooltip>
                            {po.returns_count > 0 && (
                              <Tooltip title={`${po.returns_count} phiếu trả hàng`}>
                                <Chip
                                  icon={<AssignmentReturnIcon sx={{ fontSize: 14 }} />}
                                  label={po.returns_count}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#fff3e0',
                                    color: '#e65100',
                                    fontSize: '0.7rem',
                                    height: 22
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {editingPO === po.id
                                ? formatCurrency(getEditedPOTotal(po))
                                : formatCurrency(po.total_amount)}
                            </Typography>
                            {editingPO === po.id ? (
                              <>
                                <Tooltip title="Lưu">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveCosts(po.id)}
                                    disabled={savingCosts}
                                    sx={{ color: '#1a4f0a' }}
                                  >
                                    {savingCosts ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Hủy">
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    disabled={savingCosts}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Sửa đơn giá">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEdit(po)}
                                  sx={{ color: '#666', '&:hover': { color: '#0e2a04' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {/* Expandable Row for Items */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedPOs.includes(po.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 4, backgroundColor: '#f9f9f9' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: '#333'
                                }}
                              >
                                <LocalShippingIcon sx={{ fontSize: 18 }} />
                                Chi tiết sản phẩm đã nhận
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 600, fontSize: '0.75rem', color: '#666' } }}>
                                    <TableCell>Sản phẩm</TableCell>
                                    <TableCell align="center">Đã đặt</TableCell>
                                    <TableCell align="center">Đã nhận</TableCell>
                                    <TableCell align="center">Tiến độ</TableCell>
                                    <TableCell align="right">Đơn giá</TableCell>
                                    <TableCell align="right">Thành tiền</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {po.items?.map((item) => {
                                    const progress = item.quantity_ordered > 0
                                      ? Math.min(100, (item.quantity_received_total / item.quantity_ordered) * 100)
                                      : 0;
                                    return (
                                      <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                        <TableCell>
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {item.product_name || 'N/A'}
                                            </Typography>
                                            {item.product_sku && (
                                              <Typography variant="caption" color="text.secondary">
                                                SKU: {item.product_sku}
                                              </Typography>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Typography variant="body2">{item.quantity_ordered}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: item.is_fully_received ? '#1a4f0a' : '#d4a574'
                                            }}
                                          >
                                            {item.quantity_received_total}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ minWidth: 120 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                              variant="determinate"
                                              value={progress}
                                              sx={{
                                                flex: 1,
                                                height: 6,
                                                borderRadius: 3,
                                                backgroundColor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                  backgroundColor: item.is_fully_received ? '#1a4f0a' : '#d4a574',
                                                  borderRadius: 3
                                                }
                                              }}
                                            />
                                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                                              {Math.round(progress)}%
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                          {editingPO === po.id ? (
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={editedCosts[item.id] ?? item.unit_cost}
                                              onChange={(e) => handleCostChange(item.id, e.target.value)}
                                              inputProps={{
                                                min: 0,
                                                step: 1000,
                                                style: { textAlign: 'right', fontSize: '0.875rem' }
                                              }}
                                              sx={{
                                                width: 120,
                                                '& .MuiOutlinedInput-root': {
                                                  backgroundColor: '#fff'
                                                }
                                              }}
                                            />
                                          ) : (
                                            <Typography variant="body2">{formatCurrency(item.unit_cost)}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 500,
                                              color: editingPO === po.id ? '#0e2a04' : 'inherit'
                                            }}
                                          >
                                            {editingPO === po.id
                                              ? formatCurrency(getEditedLineTotal(item))
                                              : formatCurrency(item.line_total)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {/* Receiving History Summary */}
                              {po.items?.some(item => item.receiving_history?.length > 0) && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ddd' }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: '#666', mb: 1, display: 'block' }}
                                  >
                                    Lịch sử nhận hàng:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {po.items?.flatMap(item =>
                                      item.receiving_history?.map(history => (
                                        <Chip
                                          key={history.id}
                                          size="small"
                                          label={`${item.product_name}: +${history.quantity} (${formatDate(history.date)})`}
                                          sx={{
                                            fontSize: '0.7rem',
                                            backgroundColor: '#e8f5e9',
                                            color: '#1a4f0a'
                                          }}
                                        />
                                      )) || []
                                    )}
                                  </Box>
                                </Box>
                              )}
                              {/* Returns Summary */}
                              {po.returns?.length > 0 && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ddd' }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      color: '#e65100'
                                    }}
                                  >
                                    <AssignmentReturnIcon sx={{ fontSize: 18 }} />
                                    Hàng đã trả ({po.returns.length} phiếu)
                                  </Typography>
                                  {po.returns.map((ret) => (
                                    <Box
                                      key={ret.id}
                                      sx={{
                                        p: 1.5,
                                        mb: 1,
                                        backgroundColor: '#fff3e0',
                                        borderRadius: '8px',
                                        border: '1px solid #ffe0b2'
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {ret.return_number}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                          -{formatCurrency(ret.total_return_amount)}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Ngày: {formatDate(ret.return_date)}
                                      </Typography>
                                      {ret.items?.map((item) => (
                                        <Chip
                                          key={item.id}
                                          size="small"
                                          label={`${item.quantity_returned} x ${formatCurrency(item.unit_cost)}`}
                                          sx={{
                                            ml: 1,
                                            fontSize: '0.7rem',
                                            backgroundColor: '#ffccbc',
                                            color: '#bf360c'
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                p: 8,
                textAlign: 'center'
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(26, 79, 10, 0.1) 0%, rgba(26, 79, 10, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: '#1a4f0a' }} />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1a4f0a' }}>
                Tất cả đã thanh toán!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Không có đơn hàng nào chưa thanh toán cho nhà cung cấp này.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payment Confirmation Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Xác nhận thanh toán
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Bạn đang thanh toán cho {selectedPOs.length} đơn hàng:
            </Typography>

            <Box sx={{ my: 2, maxHeight: 200, overflow: 'auto' }}>
              {selectedPOsData.map(po => (
                <Box
                  key={po.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Typography variant="body2">{po.po_number}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(po.total_amount)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 2,
                borderTop: '2px solid #0e2a04',
                mt: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Tổng cộng:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0e2a04' }}>
                {formatCurrency(getSelectedTotal())}
              </Typography>
            </Box>

            {/* Payment Reference Code */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f4f1e8', borderRadius: '8px', border: '2px dashed #0e2a04' }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                Mã tham chiếu thanh toán (dùng trong nội dung chuyển khoản):
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#0e2a04',
                    fontFamily: 'monospace',
                    letterSpacing: '0.05em'
                  }}
                >
                  {paymentReferenceCode}
                </Typography>
                <Tooltip title={copiedCode ? 'Đã sao chép!' : 'Sao chép mã'}>
                  <IconButton
                    onClick={handleCopyCode}
                    size="small"
                    sx={{
                      color: copiedCode ? '#1a4f0a' : '#666',
                      '&:hover': { color: '#0e2a04' }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Ghi chú (tùy chọn)"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              multiline
              rows={2}
              placeholder="Thêm ghi chú cho thanh toán này"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClosePaymentDialog}
            disabled={submitting}
            sx={{
              color: '#666666',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPayment}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
            sx={{
              background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
              borderRadius: '8px',
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a4f0a 0%, #0e2a04 100%)'
              }
            }}
          >
            {submitting ? 'Đang xử lý...' : 'Xác nhận thanh toán'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SupplierFinanceTab;
